import React, { useEffect } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useVisibility } from "@/contexts/VisibilityContext";
import { useTranslation } from "@/contexts/LanguageContext";
import { LockedPreview } from "@/components/access/LockedPreview";
import { Badge } from "@/components/ui/badge";

type PageGateProps = {
  moduleId: string;
  label?: string;
  lockedTitle?: string;
  lockedDescription?: string;
  children: React.ReactNode;
};

export function PageGate({ moduleId, label, lockedTitle, lockedDescription, children }: PageGateProps) {
  const { user, isAdmin } = useAuth();
  const { getVisibility } = useVisibility();
  const { t } = useTranslation();

  const visibility = getVisibility(moduleId);

  if (visibility === "hidden" && !isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  // Only show the badge if the page has a visibility setting AND user is admin
  // Don't show badge if the page is public (everyone can see it)
  // For admin users viewing member/admin/hidden pages, show the badge for awareness
  const showAdminBadge = isAdmin && visibility !== "public";

  const content = (() => {
    if (visibility === "public") return <>{children}</>;

    if (visibility === "member") {
      return user ? <>{children}</> : <LockedPreview mode="member" customTitle={lockedTitle} customDescription={lockedDescription}>{children}</LockedPreview>;
    }

    if (visibility === "admin") {
      return isAdmin ? <>{children}</> : <LockedPreview mode="admin" moduleLabel={label ?? moduleId} customTitle={lockedTitle} customDescription={lockedDescription}>{children}</LockedPreview>;
    }

    // hidden (admin)
    return <>{children}</>;
  })();

  // Determine badge label based on visibility
  const getBadgeLabel = () => {
    switch (visibility) {
      case "hidden":
        return t('visibility.hiddenAdminOnly');
      case "admin":
        return t('visibility.adminOnly');
      case "member":
        return t('visibility.membersOnly');
      default:
        return "PUBLIC";
    }
  };

  return (
    <div className="relative">
      {content}
      {showAdminBadge && (
        <div className="pointer-events-none fixed top-20 right-4 z-40">
          <Badge variant={visibility === "hidden" ? "destructive" : "secondary"}>
            {getBadgeLabel()}
          </Badge>
        </div>
      )}
    </div>
  );
}
